import Phaser from 'phaser';
import { AudioEngine } from './AudioEngine';

/** Semantic input events. Discipline scenes consume these, never raw touch. */
export type InputEvent = 'tapLeft' | 'tapRight' | 'action' | 'chargeStart' | 'chargeRelease';

/**
 * Maps device input (multitouch + dev keyboard) to semantic events.
 * - Left screen half  -> tapLeft
 * - Right screen half -> tapRight
 * - Keyboard (dev): A/Left=tapLeft, D/Right=tapRight, Space=action (hold = charge)
 *
 * Multitouch is enabled so two-thumb alternation registers every tap. The controller
 * owns its listeners and tears them down on destroy().
 */
export class InputController extends Phaser.Events.EventEmitter {
  private scene: Phaser.Scene;
  private enabled = false;
  private spaceHeld = false;
  private boundPointerDown: (p: Phaser.Input.Pointer) => void;

  constructor(scene: Phaser.Scene) {
    super();
    this.scene = scene;
    // Enough pointers for rapid two-thumb alternation.
    scene.input.addPointer(3);
    this.boundPointerDown = (p) => this.onPointerDown(p);
    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.boundPointerDown);
    this.bindKeyboard();
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    // First interaction unlocks audio on mobile.
    AudioEngine.resume();
    if (!this.enabled) return;
    // Split on the live canvas width (RESIZE mode), not a fixed design width.
    if (pointer.x < this.scene.scale.width / 2) this.emit('tapLeft');
    else this.emit('tapRight');
  }

  private bindKeyboard(): void {
    const kb = this.scene.input.keyboard;
    if (!kb) return; // touch-only devices have no keyboard plugin
    kb.on('keydown-LEFT', () => this.enabled && this.emit('tapLeft'));
    kb.on('keydown-A', () => this.enabled && this.emit('tapLeft'));
    kb.on('keydown-RIGHT', () => this.enabled && this.emit('tapRight'));
    kb.on('keydown-D', () => this.enabled && this.emit('tapRight'));
    kb.on('keydown-SPACE', () => {
      if (!this.enabled) return;
      this.emit('action');
      if (!this.spaceHeld) {
        this.spaceHeld = true;
        this.emit('chargeStart');
      }
    });
    kb.on('keyup-SPACE', () => {
      if (this.spaceHeld) {
        this.spaceHeld = false;
        this.emit('chargeRelease');
      }
    });
  }

  override destroy(): void {
    this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.boundPointerDown);
    this.scene.input.keyboard?.removeAllListeners();
    super.destroy();
  }

  override emit(event: InputEvent): boolean {
    return super.emit(event);
  }
}
